// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import type { ColumnDef } from '@tanstack/react-table';
import {
  CheckCircle2,
  Eye,
  History,
  Lock,
  RefreshCw,
  ShieldAlert,
  ShieldX,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { getApiCall } from '@/service/httpCall';
import { API_ACL_LOCK_AUDIT } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import {
  MAX_RANGE_MONTHS,
  firstOfThisMonth,
  formatAuditDateTime,
  maxEndFor,
  minStartFor,
  toInputDate,
} from './auditDateRange';

const PAGE_SIZE = 100;

const ROLE_LABELS = {
  [USER_ROLES.SUPERADMIN]: 'Super Admin',
  [USER_ROLES.SUB_SUPERADMIN]: 'Sub Super Admin',
  [USER_ROLES.RESELLER]: 'Reseller',
  [USER_ROLES.COMPANY_ADMIN]: 'Company Admin',
  [USER_ROLES.CALL_CENTRE_ADMIN]: 'Call Center Admin',
  [USER_ROLES.CALL_CENTRE_TEAM_LEAD]: 'Team Lead',
  [USER_ROLES.CALL_CENTRE_AGENT]: 'Agent',
};

const roleLabel = (role: any) => ROLE_LABELS[role] || role || '—';

/** Audit action -> what a reader should understand it as. */
const ACTION_LABELS = {
  edit: 'Edit frozen ACL',
  delete: 'Delete frozen ACL',
  lock: 'Freeze ACL',
  unlock: 'Unfreeze ACL',
  set_password: 'Set freeze password',
  reveal_password: 'Reveal freeze password',
};

const ACTION_FILTERS = [
  { value: 'all', label: 'All actions' },
  ...Object.entries(ACTION_LABELS).map(([value, label]) => ({ value, label })),
];

/** details.reason -> plain wording, so the table reads without the JSON. */
const REASON_LABELS = {
  not_super_admin: 'Not a Super Admin',
  password_not_set: 'No freeze password set yet',
  password_missing: 'Freeze password not entered',
  wrong_password: 'Wrong freeze password',
  permanently_locked: 'ACL is permanently frozen',
  not_recoverable: 'Stored password could not be decrypted',
};

const actionLabel = (action: any) => ACTION_LABELS[action] || action || '—';

const outcomeText = (row: any) => {
  if (row?.success) return 'Allowed';
  const reason = parseDetails(row?.details)?.reason;
  return REASON_LABELS[reason] || (reason ? String(reason).replace(/_/g, ' ') : 'Blocked');
};

function parseDetails(details: any) {
  if (!details) return {};
  if (typeof details === 'string') {
    try {
      return JSON.parse(details);
    } catch {
      return { raw: details };
    }
  }
  return details;
}

const ResultBadge = ({ success }: any) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
      success
        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
        : 'border-rose-500/30 bg-rose-500/10 text-rose-600 dark:text-rose-400'
    }`}
  >
    {success ? <CheckCircle2 className="h-3 w-3" /> : <ShieldX className="h-3 w-3" />}
    {success ? 'Allowed' : 'Blocked'}
  </span>
);

const StatChip = ({ icon: Icon, label, value, tone }: any) => (
  <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-card px-3 py-2">
    <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}>
      <Icon className="h-4 w-4" />
    </span>
    <div className="leading-tight">
      <div className="text-sm font-semibold text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  </div>
);

const AttemptDetails = ({ entry }: any) => {
  const details = parseDetails(entry?.details);
  const rows = [
    ['When', formatAuditDateTime(entry?.created_at)],
    [
      'ACL',
      entry?.acl_name
        ? `${entry.acl_name}${entry?.acl_id ? ` (#${entry.acl_id})` : ''}`
        : entry?.acl_id
          ? `#${entry.acl_id}`
          : '—',
    ],
    ['Action', actionLabel(entry?.action)],
    ['Result', entry?.success ? 'Allowed' : `Blocked — ${outcomeText(entry)}`],
    [
      'User',
      `${entry?.performedBy?.username || `User #${entry?.user_id}`} · ${roleLabel(entry?.performedBy?.role)}`,
    ],
    ['Email', entry?.performedBy?.email || '—'],
    ['IP', entry?.ip || '—'],
    ['Device', entry?.user_agent || '—'],
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-2 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label} className="rounded-lg border border-border/60 bg-muted/30 p-2.5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="break-words text-sm text-foreground">{value}</div>
          </div>
        ))}
      </div>

      <div>
        <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
          Recorded details
        </div>
        <pre className="max-h-60 overflow-auto rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-foreground">
          {JSON.stringify(details, null, 2)}
        </pre>
      </div>
    </div>
  );
};

const AclLockAuditPage = () => {
  const user = getUser();
  const isSuperAdmin = user?.role === USER_ROLES.SUPERADMIN;

  const [startDate, setStartDate] = usePersistedState('acl-settings/AclLockAudit:startDate', () => toInputDate(firstOfThisMonth()));
  const [endDate, setEndDate] = usePersistedState('acl-settings/AclLockAudit:endDate', () => toInputDate(new Date()));
  const [action, setAction] = usePersistedState('acl-settings/AclLockAudit:action', 'all');
  const [result, setResult] = usePersistedState('acl-settings/AclLockAudit:result', 'all');
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);

  const fetchAudit = async (offset = 0, override = {}) => {
    const filters = { startDate, endDate, action, result, ...override };
    if (filters.startDate > filters.endDate) {
      toast.error('From date cannot be after To date.');
      return;
    }
    if (filters.endDate > maxEndFor(filters.startDate)) {
      toast.error(`Date range cannot be longer than ${MAX_RANGE_MONTHS} months.`);
      return;
    }
    setLoading(true);
    try {
      const query =
        `?limit=${PAGE_SIZE}&offset=${offset}` +
        `&start_date=${filters.startDate}&end_date=${filters.endDate}` +
        (filters.action !== 'all' ? `&action=${filters.action}` : '') +
        (filters.result !== 'all' ? `&success=${filters.result === 'allowed'}` : '');
      const resp = await getApiCall(`${API_ACL_LOCK_AUDIT}${query}`, {});
      const list = Array.isArray(resp?.audit) ? resp.audit : [];
      setRows((prev) => (offset === 0 ? list : [...prev, ...list]));
      setTotal(Number(resp?.total) || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    fetchAudit(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const stats = useMemo(() => {
    const blocked = rows.filter((row) => !row.success).length;
    return { loaded: rows.length, blocked, allowed: rows.length - blocked };
  }, [rows]);

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'When',
        cell: ({ row }: any) => (
          <span className="whitespace-nowrap text-sm">
            {formatAuditDateTime(row.original.created_at)}
          </span>
        ),
      },
      {
        id: 'acl',
        header: 'ACL',
        accessorFn: (row: any) => row?.acl_name || (row?.acl_id ? `#${row.acl_id}` : '—'),
        cell: ({ row }: any) => (
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-foreground">
              {row.original.acl_name || (row.original.acl_id ? `ACL #${row.original.acl_id}` : '—')}
            </div>
            {row.original.acl_id ? (
              <div className="text-xs text-muted-foreground">ID {row.original.acl_id}</div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'action',
        header: 'Action',
        accessorFn: (row: any) => actionLabel(row?.action),
        cell: ({ row }: any) => (
          <span className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground">
            <Lock className="h-3 w-3 text-amber-500" />
            {actionLabel(row.original.action)}
          </span>
        ),
      },
      {
        id: 'result',
        header: 'Result',
        accessorFn: (row: any) => (row?.success ? 'Allowed' : 'Blocked'),
        cell: ({ row }: any) => <ResultBadge success={row.original.success} />,
      },
      {
        id: 'user',
        header: 'By user',
        accessorFn: (row: any) => row?.performedBy?.username || String(row?.user_id ?? ''),
        cell: ({ row }: any) => {
          const by = row.original.performedBy;
          return (
            <div className="min-w-0">
              <div className="truncate text-sm text-foreground">
                {by?.username || by?.name || `User #${row.original.user_id}`}
              </div>
              <div className="text-xs text-muted-foreground">
                {roleLabel(by?.role)} · ID {row.original.user_id}
              </div>
            </div>
          );
        },
      },
      {
        id: 'outcome',
        header: 'Reason',
        accessorFn: (row: any) => outcomeText(row),
        cell: ({ row }: any) => (
          <span className="text-sm text-muted-foreground">{outcomeText(row.original)}</span>
        ),
      },
      {
        accessorKey: 'ip',
        header: 'IP',
        cell: ({ row }: any) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {row.original.ip || '—'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }: any) => (
          <Button variant="ghost" size="sm" onClick={() => setSelected(row.original)}>
            <Eye className="mr-1 h-4 w-4" />
            Details
          </Button>
        ),
      },
    ],
    [],
  );

  if (!isSuperAdmin) {
    return (
      <FillPage>
        <PageHeader title="ACL Lock Audit" description="Super Admin only." />
        <div className="flex flex-1 items-center justify-center">
          <div className="flex max-w-md flex-col items-center gap-2 text-center">
            <ShieldAlert className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              Only a Super Admin can view the ACL lock audit.
            </div>
          </div>
        </div>
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        compact
        title="ACL Lock Audit"
        description={`Every freeze-password attempt on a frozen ACL, newest first${
          total ? ` · ${total} entries` : ''
        } · up to ${MAX_RANGE_MONTHS} months per view`}
        actions={
          <DateRangeFilter
            compact
            value={{ from: startDate, to: endDate }}
            onChange={({ from, to }) => {
              if (!from || !to) return;
              setStartDate(from);
              setEndDate(to);
              fetchAudit(0, { startDate: from, endDate: to });
            }}
            min={minStartFor(endDate)}
            max={maxEndFor(startDate)}
            actions={
              <>
                {rows.length > 0 && rows.length < total ? (
                  <Button
                    variant="outline"
                    className="h-9"
                    onClick={() => fetchAudit(rows.length)}
                    disabled={loading}
                  >
                    <History className="mr-1 h-4 w-4" />
                    Load older
                  </Button>
                ) : null}
                <Button variant="outline" className="h-9" onClick={() => fetchAudit(0)} disabled={loading}>
                  <RefreshCw className="mr-1 h-4 w-4" />
                  Refresh
                </Button>
              </>
            }
          >
            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Action</label>
              <Select
                value={action}
                onValueChange={(next: string) => {
                  setAction(next);
                  fetchAudit(0, { action: next });
                }}
              >
                <SelectTrigger className="h-9 w-48">
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  {ACTION_FILTERS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Result</label>
              <Select
                value={result}
                onValueChange={(next: string) => {
                  setResult(next);
                  fetchAudit(0, { result: next });
                }}
              >
                <SelectTrigger className="h-9 w-36">
                  <SelectValue placeholder="Result" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All results</SelectItem>
                  <SelectItem value="allowed">Allowed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </DateRangeFilter>
        }
      />

      <DataTable
        tableId="acl-setting.lock-audit:v1"
        columns={columns}
        data={rows}
        isLoading={loading}
        searchPlaceholder="Search by ACL, user, action, reason or IP…"
        emptyTitle="No lock attempts"
        emptyDescription="No frozen ACL was touched in this date range."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename={`acl-lock-audit-${startDate}-to-${endDate}.csv`}
        pageSize={20}
        beforeTable={
          <div className="flex flex-wrap gap-2">
            <StatChip
              icon={Lock}
              label="Attempts loaded"
              value={stats.loaded}
              tone="bg-primary/10 text-primary"
            />
            <StatChip
              icon={CheckCircle2}
              label="Allowed"
              value={stats.allowed}
              tone="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
            />
            <StatChip
              icon={ShieldX}
              label="Blocked"
              value={stats.blocked}
              tone="bg-rose-500/10 text-rose-600 dark:text-rose-400"
            />
          </div>
        }
      />

      <Dialog open={selected !== null} onOpenChange={(open: boolean) => !open && setSelected(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600">
                <Lock className="h-4 w-4" />
              </span>
              {actionLabel(selected?.action)}
            </DialogTitle>
          </DialogHeader>
          {selected ? <AttemptDetails entry={selected} /> : null}
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default AclLockAuditPage;
